/**
 * @fileoverview Centralized API service layer for UI application
 *
 * This module provides a unified API client with consistent error handling,
 * logging, and request/response processing across the entire UI application.
 *
 * Features:
 * - Centralized HTTP client with automatic error handling
 * - Consistent request logging and debugging
 * - Automatic JSON serialization/deserialization
 * - Configurable base URL for different environments
 * - Integration with UI error handling utilities
 *
 * Usage:
 *   import { apiClient } from '../services/api.ts';
 *   const users = await apiClient.get('/users');
 *   const newUser = await apiClient.post('/users', userData);
 */

import { getWorkspaceApiBaseUrl } from "../settings.ts";
import { logger } from "../utils/logger.ts";
import type { Session } from "@jkast90/kast-common/auth";
import { session as appSession } from "../../auth/session";
import {
  ApiError,
  createApiClient,
  type ApiClient as AuthedApiClient,
} from "@jkast90/kast-common/api-client";
import {
  DEFAULT_TIMEOUT_MS,
  RequestTimeoutError,
  UNREACHABLE_MESSAGE,
} from "@jkast90/kast-common/fetch";
import { redactValue } from "@jkast90/kast-common/diagnostics";

// Type definitions
export interface ApiResponse<T = unknown> {
  data?: T;
  status: number;
  statusText: string;
}

export interface RequestOptions {
  method?: string;
  headers?: Record<string, string>;
  body?: string;
  signal?: AbortSignal;
  /** Defaults to DEFAULT_TIMEOUT_MS (8 s). */
  timeoutMs?: number;
}

export interface RequestParams {
  [key: string]: string | number | boolean | undefined | string[];
}

/**
 * Only these methods are retried after a 5xx or a network error: a repeat of
 * them changes nothing on the server. A POST, PUT, PATCH or DELETE is sent
 * once (the single retry after a 401 token refresh aside, which repeats a
 * request the server refused without acting on it).
 */
const RETRYABLE_METHODS = new Set(["GET", "HEAD"]);
export const MAX_RETRIES = 2;
const RETRY_BASE_DELAY_MS = 300;

export function isRetryableMethod(method: string | undefined): boolean {
  return RETRYABLE_METHODS.has((method || "GET").toUpperCase());
}

/** Headers safe to print: the Authorization header and other secrets are replaced. */
function loggableHeaders(headers: RequestInit["headers"]): unknown {
  return headers ? redactValue(headers) : headers;
}

export interface ApiClientOptions {
  /** Base delay between retries; doubled each time. Tests pass 0. */
  retryDelayMs?: number;
  /** The session whose bearer is sent; default the app's. */
  session?: Session;
}

/** Thrown when the API refuses the session (401) and the session can't be renewed. */
export const AUTH_EXPIRED_MESSAGE =
  "Authentication expired. Please log in again.";

const bearerHeader = (bearer: string | null): Record<string, string> =>
  bearer ? { Authorization: `Bearer ${bearer}` } : {};

/**
 * Sends a request with the session's bearer (the id token, which carries the
 * email the API reads). On a 401 the session refreshes once, if that can
 * help, and the request is sent again with the new bearer; the response is
 * returned either way. A session that can't be renewed ends there, and the
 * route gates show "session ended".
 */
export async function fetchWithSession(
  send: (authHeaders: Record<string, string>) => Promise<Response>,
  session: Session = appSession,
): Promise<Response> {
  const bearer = session.bearer();
  const response = await send(bearerHeader(bearer));
  if (
    response.status !== 401 ||
    !bearer ||
    !(await session.onUnauthorized(bearer))
  )
    return response;
  logger.info("Retrying the request with the refreshed session");
  return send(bearerHeader(session.bearer()));
}

/**
 * Base API client providing HTTP operations with consistent error handling.
 *
 * This class encapsulates all HTTP communication with the backend API,
 * providing a clean interface for making requests while handling common
 * concerns like error processing, logging, and response formatting.
 */
export class ApiClient {
  private session: Session;
  private client: AuthedApiClient;

  constructor(options: ApiClientOptions = {}) {
    this.session = options.session ?? appSession;
    // The mechanics (the bearer, one refresh and resend after a 401, the time
    // limit, GET and HEAD retried after a 5xx or a network error with backoff,
    // never a POST, and never a timeout) are kast-common's api-client. The API
    // log is the fetch recorder's (main.tsx installs it). This is the wording.
    this.client = createApiClient({
      baseUrl: () => this.getDefaultBaseUrl(),
      session: this.session,
      retry: {
        retries: MAX_RETRIES,
        delayMs: options.retryDelayMs ?? RETRY_BASE_DELAY_MS,
        backoff: 2,
        timeouts: false,
      },
      recordCalls: false,
      onNetworkFailure: (error) => error as Error,
      messages: {
        timeout: UNREACHABLE_MESSAGE,
        expired: AUTH_EXPIRED_MESSAGE,
        refreshFailed: AUTH_EXPIRED_MESSAGE,
      },
      // An ApiError, so its status is read directly (statusOf, apiErrorStatus);
      // the message text is as it always was: pages and tests match on it.
      failure: async (response, fallback, authorized) => {
        const text = await response.text();
        // Still refused after the session's one refresh (or it couldn't refresh).
        if (authorized && response.status === 401)
          return new ApiError(fallback, response.status);
        return new ApiError(
          `API Error ${response.status}: ${text}`,
          response.status,
        );
      },
    });
  }

  private getDefaultBaseUrl(): string {
    try {
      return getWorkspaceApiBaseUrl();
    } catch {
      // Fallback for testing or when settings are not available
      // Use current protocol and hostname with appropriate port
      // const protocol = window.location.protocol;
      const protocol = "https:";
      const hostname = window.location.hostname;
      const port = protocol === "https:" ? "8443" : "8000";
      return `${protocol}//${hostname}:${port}/api`;
    }
  }

  async request<T = unknown>(
    endpoint: string,
    options: RequestOptions = {},
  ): Promise<T> {
    // Always get the latest base URL from settings/localStorage on every request
    const baseUrl = this.getDefaultBaseUrl();
    const url = `${baseUrl}${endpoint}`;

    const { timeoutMs = DEFAULT_TIMEOUT_MS, ...fetchOptions } = options;
    const config: RequestInit = {
      headers: {
        "Content-Type": "application/json",
        ...fetchOptions.headers,
      },
      ...fetchOptions,
    };

    // Every request, in development only, with the body redacted; a failure
    // is logged in full below.
    logger.api(`${config.method || "GET"} ${url}`, parseBody(options.body));

    try {
      const response = await this.client.optionalRequest(endpoint, config, {
        timeoutMs,
      });

      // Handle 204 No Content responses
      if (response.status === 204) {
        return null as T;
      }

      // Handle different response types
      const contentType = response.headers.get("content-type");
      let responseData: T;
      let responseText: string | undefined;
      if (contentType?.includes("application/json")) {
        responseText = await response.text();
        responseData = responseText ? JSON.parse(responseText) : (null as T);
        if (endpoint.includes("/preferences")) {
          logger.debug("Preferences response", {
            endpoint,
            data: responseData,
            status: response.status,
          });
        }
      } else {
        responseText = await response.text();
        responseData = responseText as T;
      }

      return responseData;
    } catch (thrown) {
      // A request that ran out of time reaches callers as the timeout it is.
      const error =
        thrown instanceof ApiError &&
        thrown.cause instanceof RequestTimeoutError
          ? thrown.cause
          : thrown;
      // Enhanced error logging with full request context
      console.error("[API Request Failed]", {
        url,
        method: config.method || "GET",
        headers: loggableHeaders(config.headers),
        body: parseBody(options.body),
        error:
          error instanceof Error
            ? {
                name: error.name,
                message: error.message,
                stack: error.stack,
              }
            : error,
        protocol: new URL(url).protocol,
        hasTrailingSlash: url.endsWith("/"),
      });

      logger.error(
        `API request failed: ${config.method || "GET"} ${url}`,
        error,
      );
      throw error;
    }
  }

  // HTTP method helpers
  async get<T = unknown>(
    endpoint: string,
    params: RequestParams = {},
  ): Promise<T> {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        if (Array.isArray(value)) {
          value.forEach((v) => query.append(key, String(v)));
        } else {
          query.append(key, String(value));
        }
      }
    });
    const queryString = query.toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    return this.request<T>(url, { method: "GET" });
  }

  async post<T = unknown>(endpoint: string, data: unknown = {}): Promise<T> {
    return this.request<T>(endpoint, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async put<T = unknown>(endpoint: string, data: unknown = {}): Promise<T> {
    return this.request<T>(endpoint, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async patch<T = unknown>(endpoint: string, data: unknown = {}): Promise<T> {
    return this.request<T>(endpoint, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  async delete<T = unknown>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: "DELETE" });
  }
}

/** A JSON request body, redacted for logging; non-JSON bodies are not kept. */
function parseBody(body: RequestInit["body"] | undefined): unknown {
  if (typeof body !== "string") return undefined;
  try {
    return redactValue(JSON.parse(body));
  } catch {
    return undefined;
  }
}

// Create singleton instance
export const apiClient = new ApiClient();
